import { Box, Flex, Text } from 'mds-web-ui'
import { MDXProvider } from '@mdx-js/react'
import { Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import React from 'react'
import { ArrowLeft } from 'react-feather'
import styled from 'styled-components'
import { border, color, space } from 'styled-system'

import { ScrollTop } from '../TopNav/ScrollTop'
import { Heading, Paragraph, fontSizes } from '../utils/Typography'
import { useDesktopBreakpoint } from '../utils/mediaQueries'
import { FlowGalleryWrapper } from './FlowGalleryWrapper'
import { TimelapseVideo } from './TimelapseVideo'
import { ZoomableImage } from './ZoomableImage'
import { useAppIcons } from '../utils/useAppIcons'
import { Spacer } from '../utils/Spacer'
import { Tag } from './Tag'
import { kebabCase } from 'lodash'

const components = {
  TimelapseVideo
}

export const FlowGalleryDetail = ({
  slug,
  title,
  author,
  affiliation,
  authorUrl,
  links,
  description,
  images,
  cover,
  date,
  children,
  tags,
  ...props
}) => {
  const isDesktop = useDesktopBreakpoint()
  const appIcons = useAppIcons()

  const coverImage = images[0]
  const collectionImages =
    images.length > 1 ? images.slice(1, images.length) : null

  const showTagsInMetadata = tags && isDesktop

  return (
    <FlowGalleryWrapper
      {...props}
      image={cover}
      title={title}
      description={description}
      slug={slug}
      pt={[4, 48]}
      pb={[4, 6]}
      appIcons={appIcons}
    >
      <Flex
        width="100%"
        alignItems="flex-start"
        justifyContent="flex-start"
        flexDirection="column"
      >
        <GalleryMetadata
          author={author}
          affiliation={affiliation}
          authorUrl={authorUrl}
          links={links}
          title={title}
          date={date}
          pb={4}
          px={[3, 3, 4]}
        >
          {showTagsInMetadata ? (
            <Flex alignItems="center" justifyContent="center" gap={3}>
              {tags.map(tag => {
                const id = kebabCase(tag)
                const key = `flowgallerydetail__${slug}__${id}`
                const to = `/flow/gallery/tag/${id}`
                return (
                  <Tag key={key} to={to}>
                    {`#${tag}`}
                  </Tag>
                )
              })}
            </Flex>
          ) : null}
        </GalleryMetadata>

        {isDesktop ? (
          <ZoomableImage image={coverImage} alt={title} />
        ) : (
          <BorderBox
            width="100%"
            borderRadius={[0, 12]}
            px={[0, 3, 4]}
            {...props}
          >
            <GatsbyImage image={coverImage} alt={title} />
          </BorderBox>
        )}

        {!showTagsInMetadata ? (
          <Flex
            alignItems="center"
            justifyContent="center"
            gap={3}
            py={3}
            px={[3, 4]}
          >
            {tags.map(tag => {
              const id = kebabCase(tag)
              const key = `flowgallerydetail__${slug}__${id}`
              const to = `/flow/gallery/tag/${id}`
              return (
                <Tag key={key} to={to}>
                  {`#${tag}`}
                </Tag>
              )
            })}
          </Flex>
        ) : null}

        {description ? (
          <Flex
            width="100%"
            pt={4}
            flexDirection="column"
            alignItems="center"
            gap={4}
            px={[0, 3, 4]}
          >
            <Paragraph
              animate={false}
              fontSize={fontSizes.body}
              color="#202425"
              m={0}
              width="100%"
              maxWidth={['100%', '65ch']}
              fontWeight="normal"
              textAlign={['left', 'center']}
              px={[3, 0]}
            >
              {description}
            </Paragraph>

            <MDXProvider components={components}>{children}</MDXProvider>
          </Flex>
        ) : null}

        {collectionImages ? (
          <>
            <Spacer pt={4} pb={4} width="75%" />

            <Flex flexDirection="column" gap={3} width="100%">
              {collectionImages.map((image, i) => {
                const key = `flowgallerydetail__${slug}__${i}`
                return isDesktop ? (
                  <ZoomableImage image={image} alt={title} key={key} />
                ) : (
                  <BorderBox
                    width="100%"
                    borderRadius={[0, 12]}
                    px={[0, 3, 4]}
                    key={key}
                    {...props}
                  >
                    <GatsbyImage image={image} alt={title} />
                  </BorderBox>
                )
              })}
            </Flex>
          </>
        ) : null}
      </Flex>

      <ScrollTop appIcons={appIcons} showBundleLink />
    </FlowGalleryWrapper>
  )
}

const BorderBox = styled(Box)(
  {
    overflow: 'hidden',
    position: 'relative',
    '*': {
      borderRadius: 'inherit'
    }
  },
  border
)

const GalleryMetadata = ({
  to = '/flow/gallery/',
  title,
  author,
  affiliation,
  authorUrl,
  children,
  ...props
}) => {
  const isDesktop = useDesktopBreakpoint()

  return (
    <Flex
      width="100%"
      gap={3}
      justifyContent={['flex-start', 'space-between']}
      alignItems="center"
      {...props}
    >
      <Flex gap={3} alignItems="center">
        {isDesktop ? (
          <StyledLink to={to}>
            <ArrowLeft />
          </StyledLink>
        ) : null}

        <Flex flexDirection="column" gap={1}>
          <Heading fontSize={fontSizes.copy} m={0} color="#202425">
            {title}
          </Heading>

          <Flex gap={1} alignItems="center">
            {author ? (
              <SubText
                as={authorUrl ? 'a' : 'div'}
                href={authorUrl}
                color="#3a3f42"
                rel="noopener noreferrer nofollower"
              >
                {author}
              </SubText>
            ) : null}

            {affiliation ? (
              <>
                {author ? (
                  <SubText color="#3a3f42" css={{ opacity: 0.75 }}>
                    for
                  </SubText>
                ) : null}
                <SubText
                  as={affiliation.url ? 'a' : 'div'}
                  href={affiliation.url}
                  color="#3a3f42"
                  rel="noopener noreferrer nofollower"
                >
                  {affiliation.title}
                </SubText>
              </>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
      <Flex flex={1} />

      {children}
    </Flex>
  )
}

const SubText = styled(Text)(
  {
    fontSize: fontSizes.body,
    fontWeight: 'normal',
    textDecoration: 'none'
  },
  color
)

const StyledLink = styled(Link)(
  {
    textDecoration: 'none',
    color: 'inherit'
  },
  space
)
